import { ref, readonly } from 'vue';
import { useI18n } from 'vue-i18n';
import { MAX_CHUNK_SIZE } from '../config';
import { ChatUserType } from "../enums/ChatUserType";
import { SnapshotChunkType } from "../enums/SnapshotChunkType";
import { useIncidentHub } from '@/composables/useIncidentHub';

export type PendingUploadStatus = 'uploading' | 'error';

export interface PendingUpload {
  id: string;
  status: PendingUploadStatus;
  previewUrl: string;
  errorMessage?: string;
}

// Shared state for pending uploads
const pendingUploads = ref<PendingUpload[]>([]);

/**
 * Composable for handling snapshot/image uploads.
 * Provides reactive state for tracking upload progress as pending messages.
 */
export function useSnapshot() {
  const { t } = useI18n();
  const incidentHub = useIncidentHub();

  /**
   * Splits Blob snapshot into smaller chunks (of size MAX_CHUNK_SIZE)
   * and sends them as signaling messages.
   * First is sent "start" type which indicates
   * that there will be a snapshot incoming.
   * All snapshots also have ID which is just timestamp.
   * WARNING: SignalR does not have in-order nor guaranteed delivery
   *  -> if there will be any problems with it then custom ACK mechanism with resend has to be written
   */
  async function sendSnapshot(snapshot: Blob) {
    const snapshotId = Date.now().toString();
    const previewUrl = URL.createObjectURL(snapshot);

    // Add pending upload to the list
    pendingUploads.value.push({
      id: snapshotId,
      status: 'uploading',
      previewUrl
    });

    try {
      const arrayBuffer = await snapshot.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      // 1) Start message
      await incidentHub.sendSnapshotChunk({
        snapshotId,
        type: SnapshotChunkType.Start,
        mimeType: snapshot.type,
        chunkData: '',
        senderType: ChatUserType.Caller
      });

      // 2) Send chunks
      const chunkPromises: Promise<void>[] = [];

      for (let i = 0; i < bytes.length; i += MAX_CHUNK_SIZE) {
        const chunk = bytes.slice(i, i + MAX_CHUNK_SIZE);

        chunkPromises.push(
          incidentHub.sendSnapshotChunk({
            snapshotId,
            type: SnapshotChunkType.Chunk,
            mimeType: snapshot.type,
            chunkData: toBase64(chunk),
            senderType: ChatUserType.Caller
          })
        );
      }

      // Wait for all chunks to be sent
      await Promise.all(chunkPromises);

      // 3) End message
      await incidentHub.sendSnapshotChunk({
        snapshotId,
        mimeType: snapshot.type,
        chunkData: '',
        type: SnapshotChunkType.End,
        senderType: ChatUserType.Caller
      });

      // Remove immediately - the server will send the real message
      removePendingUpload(snapshotId);
      URL.revokeObjectURL(previewUrl);
    } catch (error) {
      console.error("SignalR send error:", error);
      updatePendingUpload(snapshotId, { status: 'error', errorMessage: t('chat.uploadFailed') });

      // Keep error visible for a bit then remove
      setTimeout(() => {
        removePendingUpload(snapshotId);
        URL.revokeObjectURL(previewUrl);
      }, 5000);
    }
  }

  return {
    pendingUploads: readonly(pendingUploads),
    sendSnapshot
  };
}

function updatePendingUpload(id: string, updates: Partial<PendingUpload>) {
  const upload = pendingUploads.value.find(u => u.id === id);
  if (upload) {
    Object.assign(upload, updates);
  }
}

function removePendingUpload(id: string) {
  const index = pendingUploads.value.findIndex(u => u.id === id);
  if (index !== -1) {
    pendingUploads.value.splice(index, 1);
  }
}

function toBase64(uint8: Uint8Array) {
  let binary = "";
  for (let i = 0; i < uint8.length; i++) {
    binary += String.fromCharCode(uint8[i]);
  }
  return btoa(binary);
}
